import { useEffect, useState } from 'react';
import { ApiError, OfflineError, api } from '../lib/api';
import { useTerminal } from '../lib/store';
import { Numpad, PinDisplay } from '../components/Numpad';
import { Dot } from '../components/ui';
import { clockTime, longDate } from '../lib/format';

type Identidades = Awaited<ReturnType<typeof api.demoIdentities>>;

export function Login({ tenantName }: { tenantName: string | null }) {
  const { login, demoLogin, busy, online, queued, notify } = useTerminal();
  const [pin, setPin] = useState('');
  const [now, setNow] = useState(new Date());
  const [demo, setDemo] = useState<Identidades | null>(null);

  // Solo aparece si el cliente tiene activado el acceso de demostración; en
  // cualquier otro, el endpoint responde 404 y aquí no se pinta nada.
  useEffect(() => {
    api.demoIdentities().then(setDemo).catch(() => setDemo(null));
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const submit = async () => {
    if (!pin) return;
    try {
      await login(pin);
      setPin('');
    } catch (err) {
      setPin('');
      if (err instanceof OfflineError) {
        notify({
          kind: 'error',
          title: 'Sin conexión con el servidor',
          body: 'No se puede identificar a un operario sin red. Avisa a mantenimiento.',
        });
      } else {
        notify({ kind: 'error', title: 'PIN incorrecto', body: err instanceof ApiError ? err.message : undefined });
      }
    }
  };

  return (
    <div className="app">
      <div className="content" style={{ display: 'grid', placeItems: 'center', alignContent: 'center' }}>
        {demo && (demo.operario || demo.jefe) ? (
          <div className="demo-bar">
            <div className="demo-bar__titulo">
              <span className="pill pill--warn">Demostración</span>
              <span className="muted small">Entra sin PIN con cualquiera de los dos papeles del terminal</span>
            </div>
            <div className="demo-bar__botones">
              {demo.operario ? (
                <button
                  className="action action--accent"
                  disabled={busy}
                  onClick={() =>
                    void demoLogin(demo.operario!.employeeId).catch((err: unknown) =>
                      notify({ kind: 'error', title: 'No se pudo entrar', body: (err as Error).message }),
                    )
                  }
                >
                  <span className="action__label">👷 Entrar como operario</span>
                  <span className="action__hint">
                    {demo.operario.fullName} · ficha, inicia y para fases
                  </span>
                </button>
              ) : null}
              {demo.jefe ? (
                <button
                  className="action action--ok"
                  disabled={busy}
                  onClick={() =>
                    void demoLogin(demo.jefe!.employeeId).catch((err: unknown) =>
                      notify({ kind: 'error', title: 'No se pudo entrar', body: (err as Error).message }),
                    )
                  }
                >
                  <span className="action__label">🧑‍🔧 Entrar como jefe de producción</span>
                  <span className="action__hint">
                    {demo.jefe.fullName} · además finaliza fases y cierra órdenes
                  </span>
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="login-grid">
          <div className="stack">
            <div className="eyebrow">{tenantName ? `${tenantName} · SAP Business One` : 'ECINSA · SAP Business One'}</div>
            <h1 className="h1" style={{ fontSize: 44 }}>Terminal de producción</h1>
            <p className="muted" style={{ fontSize: 19, maxWidth: 440 }}>
              Identifícate con tu PIN para fichar la entrada e imputar tiempos en las órdenes de fabricación.
            </p>

            <div className="card card--flat" style={{ maxWidth: 440 }}>
              <div className="mono" style={{ fontSize: 56, fontWeight: 300, letterSpacing: '-0.03em' }}>
                {clockTime(now)}
              </div>
              <div className="muted capitalize-first">{longDate(now)}</div>
            </div>

            <div className="row row--wrap">
              <a className="link small" href="/direccion">
                ¿Eres responsable de planta? Entra con tu usuario ↗
              </a>
            </div>

            <div className="row">
              <div className="conn">
                <Dot tone={online ? 'ok' : 'danger'} />
                {online ? 'Conectado con SAP' : 'Sin conexión · modo local'}
              </div>
              {queued > 0 ? <span className="queue-badge">⇅ {queued} acciones pendientes de enviar</span> : null}
            </div>
          </div>

          <div className="card stack">
            <PinDisplay length={pin.length} placeholder="Introduce tu PIN" />
            <Numpad value={pin} onChange={setPin} onSubmit={submit} disabled={busy} submitLabel={busy ? '…' : 'Entrar'} />
          </div>
        </div>
      </div>
    </div>
  );
}
